import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useMeta from '../hooks/useMeta';

export default function Home() {
  useMeta(
    'Danry Diamant Boring ApS',
    'Professionel diamantboring til ventilation, installationer og specialopgaver. Danry Diamant Boring ApS udfører præcise boringer for private, erhverv og entreprenører.'
  );

  const services = [
    {
      title: 'Diamantboring',
      eyebrow: '01 / Beton & Murværk',
      description: 'Præcise boringer i beton, murværk og andre hårde materialer.',
      link: '/diamantboring',
    },
    {
      title: 'Ventilationsboringer',
      eyebrow: '02 / Luft & Klima',
      description: 'Huller til ventilationsanlæg i både boliger og erhverv.',
      link: '/diamantboring',
    },
    {
      title: 'Installationer',
      eyebrow: '03 / Rør & Kabler',
      description: 'Præcise rørgennemføringer og huller til tekniske installationer.',
      link: '/diamantboring',
    },
    {
      title: 'Specialopgaver',
      eyebrow: '04 / Skræddersyet',
      description: 'Tilpassede og komplekse løsninger efter projektets specifikke behov.',
      link: '/diamantboring',
    },
  ];

  const usps = [
    {
      title: 'Høj præcision',
      description: 'Vi borer med millimeterpræcision efter dine nøjagtige specifikationer.',
      icon: 'fa-solid fa-bullseye',
    },
    {
      title: 'Professionelt udstyr',
      description: 'Vi anvender det nyeste og mest effektive diamantboreudstyr på markedet.',
      icon: 'fa-solid fa-gears',
    },
    {
      title: 'Hurtig responstid',
      description: 'Vi rykker hurtigt ud og overholder altid de aftalte tidsrammer.',
      icon: 'fa-solid fa-bolt',
    },
    {
      title: 'Fleksible løsninger',
      description: 'Ingen opgave er for lille eller for kompleks – vi finder altid en løsning.',
      icon: 'fa-solid fa-sliders',
    },
    {
      title: 'Fokus på kvalitet',
      description: 'Vi leverer et rent og professionelt stykke arbejde hver eneste gang.',
      icon: 'fa-solid fa-award',
    },
    {
      title: 'Konkurrencedygtige priser',
      description: 'Professionelt håndværk til fair og gennemskuelige priser.',
      icon: 'fa-solid fa-tags',
    },
  ];

  // Cloudflare resized image URLs for the slideshow
  const heroImages = [
    'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/93766035-610889666166220-57593376840613888-n.jpg',
    'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/136091515-788338955087956-80350549020583894-n.jpg',
    'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/118428733-696760547579131-6862768956582941410-n.jpg'
  ];

  const introImage = 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/118510150-696760587579127-8102701730813165999-n.jpg';

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  return (
    <div className="pt-[90px] lg:pt-[120px]">
      {/* Hero Section */}
      <section className="relative flex flex-col bg-neutral-950 overflow-hidden lg:h-[750px]">
        {/* Full-bleed Image Area */}
        <div className="relative h-[350px] sm:h-[450px] lg:absolute lg:inset-0 lg:h-full w-full z-0">
          {heroImages.map((src, index) => (
            <img
              key={src}
              src={src}
              alt={`Professionel diamantboring udført af Danry Diamant Boring ApS - Slide ${index + 1}`}
              className={`absolute inset-0 w-full h-full object-cover object-center lg:object-[65%_center] transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-neutral-950/20 z-20" />
        </div>

        {/* Slide Indicators */}
        <div className="absolute top-[310px] sm:top-[410px] lg:top-auto lg:bottom-[250px] left-1/2 -translate-x-1/2 z-30 flex gap-2 bg-neutral-950/40 backdrop-blur-xs px-3 py-1.5 rounded-full shadow-lg">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentSlide ? 'bg-brand-orange w-6' : 'bg-white/60 hover:bg-white'
              }`}
              aria-label={`Gå til slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Sleek, compact full-width banner at the bottom of the hero section */}
        <div className="relative z-10 w-full lg:absolute lg:bottom-0 lg:left-0 lg:right-0 bg-neutral-900/90 backdrop-blur-md border-t-4 border-brand-orange shadow-2xl py-10 sm:py-12 lg:py-10 overflow-hidden">
          
          {/* Subtle geometric watermark */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.02]">
            <svg className="absolute -right-16 -bottom-16 w-96 h-96 text-white" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="0.5">
              <circle cx="50" cy="50" r="40" />
              <circle cx="50" cy="50" r="30" />
              <circle cx="50" cy="50" r="20" />
              <line x1="10" y1="50" x2="90" y2="50" />
              <line x1="50" y1="10" x2="50" y2="90" />
              <line x1="21.7" y1="21.7" x2="78.3" y2="78.3" />
              <line x1="21.7" y1="78.3" x2="78.3" y2="21.7" />
            </svg>
          </div>
 
          {/* Content Container aligned with site grid */}
          <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 w-full text-left">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-16 w-full">
              <div className="flex flex-col gap-3 max-w-3xl">
                <h1 className="text-2xl sm:text-3xl lg:text-3xl xl:text-4xl font-bold text-white leading-tight uppercase">
                  Professionel diamantboring med <span className="text-brand-orange">præcision</span>
                </h1>

                <p className="text-sm sm:text-base text-neutral-300 leading-relaxed">
                  Danry Diamant Boring ApS udfører diamantboring til ventilation, installationer og specialopgaver for både private, erhverv og entreprenører.
                </p>
              </div>

              <div className="flex-shrink-0 w-full sm:w-auto">
                <a
                  href="tel:+4522971411"
                  className="w-full sm:w-72 py-4 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-center uppercase tracking-wider transition-all duration-200 block rounded-full text-sm sm:text-base"
                >
                  Ring 22 97 14 11
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section (Moved directly under Hero) */}
      <section className="py-24 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-16">
          {/* Header Block */}
          <div className="flex flex-col items-start">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 uppercase tracking-tight mt-2">
              Hvad kan vi hjælpe dig med?
            </h2>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="block border-l-4 border-brand-orange pl-6 py-2 transition-all duration-300"
              >
                <h3 className="text-lg font-bold text-neutral-900 uppercase mb-3 transition-colors duration-200">
                  {service.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed text-sm">
                  {service.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-white border-y border-neutral-100">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 xl:col-span-5">
            <div className="relative rounded-none overflow-hidden border border-neutral-200 bg-neutral-100 aspect-4/3 shadow-xl">
              <img
                src={introImage}
                alt="Erfaren tekniker i gang med diamantboring"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-8 xl:col-span-7 flex flex-col gap-6 lg:pt-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight uppercase">
              Erfarent håndværk og moderne præcision
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              Hos Danry Diamant Boring ApS bygger vores arbejde på solid erfaring, høj faglig stolthed og moderne teknologi. Vi forstår, at præcision er altafgørende, når der skal bores i beton, murværk eller andre hårde materialer. Derfor går vi aldrig på kompromis med kvaliteten af vores arbejde.
            </p>
            <p className="text-neutral-600 leading-relaxed">
              Vi er kendt for vores store fleksibilitet og effektive udførelse. Uanset om du har brug for et enkelt hul til et emhætteaftræk i en privatbolig, eller du skal bruge en pålidelig underentreprenør til et større erhvervsbyggeri, så leverer vi varen til tiden og til aftalt pris.
            </p>
            <div className="mt-4">
              <Link
                to="/om-os"
                className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-900 hover:bg-neutral-800 text-white font-bold uppercase text-sm tracking-wider rounded-full transition-all duration-200"
              >
                Læs mere om os
                <i className="fa-solid fa-arrow-right text-brand-orange" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-orange relative overflow-hidden">
        {/* Dark half-circle: top on mobile, right side on desktop */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 sm:top-1/2 sm:left-auto sm:right-0 sm:translate-x-1/2 sm:-translate-y-1/2 w-64 h-64 sm:w-96 sm:h-96 bg-neutral-950/15 rounded-full pointer-events-none" />
        
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col gap-8 relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight uppercase">
            Skal du have udført diamantboring?
          </h2>
          <p className="text-lg text-white max-w-2xl mx-auto leading-relaxed">
            Kontakt os i dag for en uforpligtende snak om dit projekt. Vi rådgiver gerne om den bedste løsning og giver dig et skræddersyet tilbud.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-2">
            <a
              href="tel:+4522971411"
              className="px-8 py-4 rounded-full bg-white hover:bg-neutral-100 text-brand-orange font-bold text-base transition-all duration-200 shadow-lg flex items-center gap-3"
            >
              <i className="fa-solid fa-phone" />
              Ring 22 97 14 11
            </a>
            <a
              href="mailto:danrydiamant@gmail.com"
              className="px-8 py-4 rounded-full bg-transparent hover:bg-white/10 border border-white text-white font-bold text-base transition-all duration-200 flex items-center gap-3"
            >
              <i className="fa-solid fa-envelope" />
              Send email
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
